import * as React from "react";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import { ScrollableTable } from "src/components/ScrollableTable";
import { AColumn } from "src/components/tables/AsyncTable";
import { imgSrc } from "src/components/PlmUploader/imgSrc";
import { TableStore } from "src/components/tables/TableStore";
import { onError } from "src/common/onError";
import { FilterFieldsDict } from "src/components/tables/FiltersForm";
import { selectOnRowClick } from "src/components/tables/selectOnRowClick";
import { filterMonoSearch } from "src/components/tables/FiltersForm/filterMonoSearch";
import { KeepSelected } from "src/components/tables/KeepSelected";
import { DrawerPlmWithConfirmation } from "src/components/DrawerPlmWithConfirmation";
import { ZUid } from "src/types/zUid";
import styles from "./SelectBom2aMaterial.module.less";
import { loadBom2aMaterials } from "../../apiBom2aPositions";
import {
  FilterBom2aMaterial,
  ZBom2aMaterialDesc,
} from "../../Bom2aPositionsTypes";

interface PropsSelectBom2aMaterial {
  materialTypeId: ZUid;
  multi: boolean;
  open: boolean;
  close(): void;
  exec(list: ZBom2aMaterialDesc[]): Promise<void>;
  title?: string;
}

export const SelectBom2aMaterial: React.FC<PropsSelectBom2aMaterial> = observer(
  (props) => {
    const {
      multi,
      open,
      close,
      exec,
      materialTypeId,
      title = t("Material"),
    } = props;
    const [materialSelectStore, setMaterialSelectStore] =
      React.useState<TableStore<
        ZBom2aMaterialDesc,
        FilterBom2aMaterial
      > | null>(null);
    const [saving, setSaving] = React.useState(false);
    React.useEffect(() => {
      if (open) {
        setMaterialSelectStore(
          new TableStore({
            rowKey: "id",
            fnLoad: async ({ page, pageSize: size, filters }) => {
              const { content, totalElements } = await loadBom2aMaterials({
                materialTypeId,
                page,
                size,
                query: filters?.query,
              });
              return { rows: content, totalItems: totalElements };
            },
            selectionSettings: {
              selectionType: multi ? "checkbox" : "radio",
              keepSelected: true,
            },
          }),
        );
      } else {
        setMaterialSelectStore(null);
      }
    }, [open]);
    const columns: AColumn<ZBom2aMaterialDesc>[] = React.useMemo(
      () => [
        {
          key: "materialType",
          title: t("Material"),
          render: (_, row) => row.type,
        },
        {
          key: "Description",
          title: t("Heading"),
          render: (_, row) => row.description,
        },
        {
          key: "image",
          title: t("Image"),
          render: (_, row) =>
            row.image ? (
              <img
                src={imgSrc(row.image)}
                style={{ maxWidth: 64, maxHeight: 64 }}
                alt=""
              />
            ) : null,
        },
      ],
      [],
    );
    const onOk = () => {
      if (materialSelectStore?.selected.length) {
        setSaving(true);
        exec(materialSelectStore.selected)
          .then(close)
          .finally(() => setSaving(false))
          .catch(onError);
      }
    };
    const filterItems: FilterFieldsDict<FilterBom2aMaterial> = {
      query: filterMonoSearch(),
    };
    return (
      <DrawerPlmWithConfirmation
        drawerProps={{
          destroyOnClose: true,
          title,
          open: !!materialSelectStore,
          onClose: close,
        }}
        confirmationProps={{
          okText: t("Select"),
          okButtonProps: {
            onClick: onOk,
            disabled: !materialSelectStore?.selected.length,
            loading: saving,
          },
          cancelButtonProps: {
            onClick: close,
          },
        }}
      >
        {!!materialSelectStore && (
          <div className={styles.tableBox}>
            <KeepSelected
              store={materialSelectStore}
              placeholder={t(multi ? "Selected positions" : "Selected")}
              makeTag={(r) => r.description}
            />
            <ScrollableTable
              store={materialSelectStore}
              columns={columns}
              filterItems={filterItems}
              onRowClick={(row) => selectOnRowClick(row, materialSelectStore)}
            />
          </div>
        )}
      </DrawerPlmWithConfirmation>
    );
  },
);
